import { useEffect, useState } from 'react'
import { api } from '../api/client'
import type { ChangeFeedPage } from '../api/types'
import { getScope, type Scope } from '../auth/scope'

/*
  The reference read model (ADR 0020, replacing the IndexedDB store of ADR 0005).

  The chute, calving, move, notes and map screens need the ranch's animals (with their tags),
  herds, products, pastures, parcels, water and fences, settings and the ranch record. The API
  serves each as a change feed, GET /api/v1/sync/changes?collection=&since=. This module keeps
  what the feeds returned in memory, per ranch, for the life of the page:

    - the first read for a ranch pulls every collection from since=0;
    - refreshReference() pulls what changed since, after every saved record (commands.ts) and
      whenever a screen asks for fresh data (the map after an import);
    - screens subscribe with useReferenceVersion() and re-read when it moves.

  Nothing is written to the browser. A reload starts from the feeds again, which is a few
  requests for a ranch's reference data; there is no offline copy to keep consistent.
*/

export const REFERENCE_COLLECTIONS = [
  'settings',
  'theme',
  'ranches',
  'parcels',
  'landunits',
  'measurements',
  'waterpoints',
  'infrastructure',
  'animals',
  'herds',
  'products',
] as const

export type Collection = (typeof REFERENCE_COLLECTIONS)[number]

interface RanchModel {
  collections: Map<string, Map<string, unknown>>
  since: Map<string, string>
  /** Every current identifier, normalised, to the animals carrying it. */
  tags: Map<string, Set<string>>
  loaded: boolean
}

const models = new Map<string, RanchModel>()
const inFlight = new Map<string, Promise<void>>()
const listeners = new Set<() => void>()
let version = 0

const keyOf = (scope: Scope) => `${scope.organizationId}/${scope.ranchId}`

function modelFor(scope: Scope): RanchModel {
  const key = keyOf(scope)
  let model = models.get(key)
  if (!model) {
    model = { collections: new Map(), since: new Map(), tags: new Map(), loaded: false }
    models.set(key, model)
  }
  return model
}

function emit(): void {
  version += 1
  for (const listener of listeners) listener()
}

function reindexTags(model: RanchModel, animalPublicId: string, data: unknown): void {
  for (const holders of model.tags.values()) holders.delete(animalPublicId)
  const identifiers = (data as { identifiers?: { normalized: string }[] } | null)?.identifiers ?? []
  for (const identifier of identifiers) {
    let holders = model.tags.get(identifier.normalized)
    if (!holders) {
      holders = new Set()
      model.tags.set(identifier.normalized, holders)
    }
    holders.add(animalPublicId)
  }
}

/** Pulls what changed in each collection since the last pull for this ranch. */
export async function pullChanges(
  scope: Scope,
  collections: readonly string[] = REFERENCE_COLLECTIONS,
): Promise<number> {
  const model = modelFor(scope)
  let received = 0
  await Promise.all(
    collections.map(async (collection) => {
      let rows = model.collections.get(collection)
      if (!rows) {
        rows = new Map()
        model.collections.set(collection, rows)
      }
      // The feed starts at 0; the API binds `since` as a BIGINT and refuses an empty value.
      let since = model.since.get(collection) || '0'
      // A feed answers in pages; follow until nextSince stops moving.
      for (let page = 0; page < 20; page++) {
        const query = new URLSearchParams({ collection, since })
        const { data } = await api.get<ChangeFeedPage>(`/api/v1/sync/changes?${query}`, {
          scope,
        })
        for (const item of data.items ?? []) {
          received += 1
          if (collection === 'animals') {
            reindexTags(model, item.publicId, item.op === 'Delete' ? null : item.data)
          }
          if (item.op === 'Delete') rows.delete(item.publicId)
          else rows.set(item.publicId, item.data)
        }
        const moved = data.nextSince && data.nextSince !== since
        since = data.nextSince ?? since
        model.since.set(collection, since)
        if (!moved || (data.items ?? []).length === 0) break
      }
    }),
  )
  model.loaded = true
  return received
}

/**
 * Brings the read model for a ranch up to date. Concurrent calls for one ranch share a pull;
 * listeners are told when it lands. Failures are thrown to the caller.
 */
export function refreshReference(scope: Scope | null = getScope()): Promise<void> {
  if (!scope) return Promise.resolve()
  const key = keyOf(scope)
  const running = inFlight.get(key)
  if (running) return running
  const run = pullChanges(scope)
    .then(() => emit())
    .finally(() => inFlight.delete(key))
  inFlight.set(key, run)
  return run
}

/** The first read for a ranch loads it; later reads use what is held. */
async function ensureLoaded(scope: Scope): Promise<RanchModel> {
  const model = modelFor(scope)
  if (!model.loaded) await refreshReference(scope)
  return model
}

/** Every record in a collection, as the feed last gave it. */
export async function listCollection<T>(scope: Scope, collection: Collection): Promise<T[]> {
  const model = await ensureLoaded(scope)
  return [...(model.collections.get(collection)?.values() ?? [])].filter(
    (d): d is T => d !== null && d !== undefined,
  )
}

/** One record by id, or undefined. */
export async function getRecord<T>(
  scope: Scope,
  collection: Collection,
  publicId: string,
): Promise<T | undefined> {
  const model = await ensureLoaded(scope)
  return model.collections.get(collection)?.get(publicId) as T | undefined
}

/** The ids of the animals carrying a normalised identifier. */
export async function animalsWithTag(scope: Scope, normalized: string): Promise<string[]> {
  const model = await ensureLoaded(scope)
  return [...(model.tags.get(normalized) ?? [])]
}

/** Moves whenever the read model changes; a screen re-reads when it does. */
export function useReferenceVersion(): number {
  const [current, setCurrent] = useState(version)
  useEffect(() => {
    const listener = () => setCurrent(version)
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  }, [])
  return current
}

/** Sign-out and tests: forget every ranch. */
export function clearReference(): void {
  models.clear()
  inFlight.clear()
  emit()
}
